import { useState, useEffect } from 'react';
import { GameSettings } from '../types';
import { THEME_MAP, ThemeType } from '../constants';
import { detectBrowserLanguage } from '../i18n';

const STORAGE_KEY = 'knucklebones_settings';

export const useSettings = () => {
  const [settings, setSettingsState] = useState<GameSettings>(() => ({
    diceSpeed: 'NORMAL',
    theme: 'PURPLE',
    isMuted: false,
    isMusicMuted: false,
    language: detectBrowserLanguage(),
  }));

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        setSettingsState(prev => ({ ...prev, ...JSON.parse(saved) }));
      } catch {
        console.warn('[Settings] Failed to parse saved settings, using defaults');
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Update HTML lang attribute when language changes
  useEffect(() => {
    document.documentElement.lang = settings.language === 'TR' ? 'tr' : 'en';
  }, [settings.language]);

  const applyTheme = () => {
    const theme = THEME_MAP[settings.theme as ThemeType];
    const root = document.documentElement;
    root.style.setProperty('--theme-primary', theme.primary);
    root.style.setProperty('--theme-primary-hover', theme.hover);
    root.style.setProperty('--theme-glow', theme.glow);
    root.style.setProperty('--theme-border', theme.border);
    root.style.setProperty('--theme-border-inner', theme.borderInner);
  };

  const setSettings = (updates: Partial<GameSettings> | ((prev: GameSettings) => Partial<GameSettings>)) => {
    setSettingsState(prev => {
      const newSettings = typeof updates === 'function'
        ? updates(prev)
        : updates;
      return { ...prev, ...newSettings };
    });
  };

  return { settings, setSettings, applyTheme };
};
